import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Gauge,
  Headphones,
  Infinity as InfinityIcon,
  Info,
  PhoneCall,
  Quote,
  ShieldCheck,
  Sparkles,
  Tv,
  Wallet,
  Zap,
} from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useContent } from "@/lib/hooks";
import PackageCard from "@/components/PackageCard";
import { ServiceIcon } from "@/components/ServiceIcon";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1744868562210-fffb7fa882d9?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85";

const PRICE_CHANGE_ROWS = [
  { name: "1 Aylık 8/4MBit Sınırsız İNTERNET HİZMETİ (Air-Fiber) (8Mbps)", price: "750 TL" },
  { name: "1 Aylık 10/4MBit Sınırsız İNTERNET HİZMETİ (Air-Fiber) (10Mbps)", price: "800 TL" },
  { name: "1 Aylık 12/4MBit Sınırsız İNTERNET HİZMETİ (Air-Fiber) (12Mbps)", price: "850 TL" },
  { name: "1 Aylık 16/4MBit Sınırsız İNTERNET HİZMETİ (Air-Fiber) (16Mbps)", price: "900 TL" },
  { name: "1 Aylık 32/8 MBit Sınırsız İNTERNET HİZMETİ (Air-Fiber) (32 Mbps)", price: "1200 TL" },
  { name: "1 Aylık 50/10 MBit Sınırsız İNTERNET HİZMETİ (Air-Fiber) (50 Mbps)", price: "1350 TL" },
  { name: "1 Aylık 20/20 MBit METRO Sınırsız İNTERNET HİZMETİ (Air-Fiber) (20Mbps)", price: "1900 TL" },
  { name: "1 Aylık 100/8 MBit METRO Sınırsız İNTERNET HİZMETİ (Air-Fiber) (100Mbps)", price: "1700 TL" },
  { name: "1 Aylık 100/100 MBit METRO Sınırsız İNTERNET HİZMETİ (Air-Fiber) (100Mbps)", price: "4500 TL" },
];

export default function Home() {
  const { t, ml, lang } = useLang();
  const [slide, setSlide] = useState(0);
  const { data: packages = [] } = useContent("/content/packages");
  const { data: services = [] } = useContent("/content/services");
  const { data: campaigns = [] } = useContent("/content/campaigns");
  const { data: faqs = [] } = useContent("/content/faqs");

  const slides = [
    { badge: t("hero.slide1Badge"), title: t("hero.slide1Title"), title2: t("hero.slide1Title2"), desc: t("hero.slide1Desc") },
    { badge: t("hero.slide2Badge"), title: t("hero.slide2Title"), title2: t("hero.slide2Title2"), desc: t("hero.slide2Desc") },
    { badge: t("hero.slide3Badge"), title: t("hero.slide3Title"), title2: t("hero.slide3Title2"), desc: t("hero.slide3Desc") },
  ];

  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % slides.length), 5500);
    return () => clearInterval(id);
  }, [slides.length]);

  const featureItems = [
    { icon: Wallet, title: t("home.why1Title"), desc: t("home.why1Desc") },
    { icon: Headphones, title: t("home.why3Title"), desc: t("home.why3Desc") },
    { icon: InfinityIcon, title: t("home.why2Title"), desc: t("home.why2Desc") },
    { icon: Tv, title: t("home.why4Title"), desc: t("home.why4Desc") },
  ];

  const offerFeatures = [
    t("home.offerFeat1") || t("home.why1Title"),
    t("home.offerFeat2") || t("home.why1Desc"),
    t("home.offerFeat3") || t("home.why2Title"),
    t("home.offerFeat4") || t("home.why3Title"),
  ];

  const testimonials = [
    {
      name: "Mehmet Y.",
      text:
        lang === "tr"
          ? "Silifke'de bu kadar hızlı internet beklemiyordum. Kurulum çok hızlı oldu, fiyat da çok uygun."
          : "I didn't expect such fast internet in Silifke. Installation was quick and the price is great.",
    },
    {
      name: "Ayşe K.",
      text:
        lang === "tr"
          ? "08:00 - 20:00 yerel destek gerçekten var. Sorun yaşadığımda aynı gün müdahale ettiler. Teşekkürler EntaNET!"
          : "08:00 - 20:00 local support is real. They responded the same day I had an issue. Thanks EntaNET!",
    },
    {
      name: "Hüseyin D.",
      text:
        lang === "tr"
          ? "Taahhütsüz olması çok büyük avantaj. Gizli ücret yok, ne ödediğimi biliyorum. Tavsiye ederim."
          : "Being commitment-free is a huge advantage. No hidden fees, I know what I pay. Highly recommend.",
    },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO — full-bleed image with navy overlay */}
      <section className="relative h-[600px] min-h-[600px] w-full overflow-hidden">
        {slides.map((s, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-700 ${i === slide ? "opacity-100" : "opacity-0"}`}
          >
            <img src={HERO_IMAGE} alt="" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#101436] via-[#101436]/85 to-[#101436]/40" />
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full max-w-7xl items-center px-4 sm:px-6 lg:px-8">
          <div className="grid w-full grid-cols-1 items-center gap-10 lg:grid-cols-12">
            {/* LEFT — slide text + CTAs */}
            <div className="lg:col-span-7">
              {slides.map((s, i) => (
                <div key={i} className={i === slide ? "block animate-slide-fade" : "hidden"}>
                  <span className="inline-flex items-center gap-2 rounded-full bg-[#ff9800]/20 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#ff9800]">
                    {s.badge}
                  </span>
                  <h1 className="mt-5 font-display text-4xl font-bold leading-[1.1] text-white md:text-5xl lg:text-6xl">
                    {s.title}
                  </h1>
                  <h2 className="mt-1 font-display text-4xl font-bold leading-[1.1] text-white md:text-5xl lg:text-6xl">
                    {s.title2}
                  </h2>
                  <p className="mt-5 max-w-md text-lg leading-relaxed text-zinc-200">{s.desc}</p>
                </div>
              ))}

              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/iletisim"
                  data-testid="hero-cta-contact"
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-call px-8 py-3 text-sm font-bold text-white transition-all hover:opacity-90"
                >
                  <PhoneCall className="h-4 w-4" />
                  {t("nav.callYou")}
                </Link>
                <Link
                  to="/tarifeler"
                  data-testid="hero-cta-packages"
                  className="inline-flex items-center gap-2 rounded-full bg-[#ff9800] px-8 py-3 text-sm font-bold text-[#1d1d1f] transition-all hover:bg-[#f57c00]"
                >
                  {t("hero.ctaPackages")}
                  <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </Link>
              </div>
            </div>

            {/* RIGHT — E-Devlet Online Başvuru card */}
            <div className="lg:col-span-5">
              <div className="rounded-3xl border border-[#ff9800]/30 bg-gradient-to-br from-white/[0.12] to-white/[0.05] p-7 backdrop-blur-xl shadow-2xl shadow-black/20">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#ff9800]/20 text-[#ff9800]">
                    <ShieldCheck className="h-6 w-6" />
                  </span>
                  <div>
                    <p className="font-display text-lg font-bold text-white">{t("hero.onlineBasvuruTitle")}</p>
                    <p className="text-xs text-zinc-400">{t("hero.onlineBasvuruDesc")}</p>
                  </div>
                </div>

                <ul className="mt-5 space-y-2.5">
                  {[t("hero.onlineBasvuruFeat1"), t("hero.onlineBasvuruFeat2"), t("hero.onlineBasvuruFeat3")].map((feat, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-200">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#ff9800]" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="https://entanet.com.tr/online_basvuru/#/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="online-basvuru-cta"
                  className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#ff9800] px-6 text-sm font-bold text-[#1d1d1f] transition-colors hover:bg-[#f57c00]"
                >
                  {t("hero.onlineBasvuruBtn")}
                  <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </a>

                <div className="mt-4 flex items-center justify-center gap-5 border-t border-white/10 pt-4 text-xs text-zinc-400">
                  <span className="flex items-center gap-1.5">
                    <Zap className="h-3.5 w-3.5 text-[#ff9800]" /> 100 Mbps
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Headphones className="h-3.5 w-3.5 text-[#ff9800]" /> 08:00-20:00
                  </span>
                  <span className="flex items-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5 text-[#ff9800]" /> e-Devlet
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Slider dots */}
        <div className="absolute bottom-7 left-0 right-0 z-10 flex justify-center gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-[9px] rounded-full transition-all duration-300 ${
                i === slide ? "w-[26px] bg-[#ff9800]" : "w-[9px] bg-white/45 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      </section>

      {/* FEATURE CARDS — Netspeed highlight strip */}
      <section className="relative z-10 mx-auto -mt-10 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="home-features">
          {featureItems.map((item) => (
            <div
              key={item.title}
              className="rounded-2xl border border-zinc-100 bg-white p-6 text-center shadow-sm transition-shadow hover:shadow-lg"
            >
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#f3f0ff] text-[#4F46E5]">
                <item.icon className="h-7 w-7" />
              </span>
              <h3 className="mt-4 font-display text-sm font-bold text-[#101436]">{item.title}</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-gray-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* OFFER HIGHLIGHT — Netspeed pricing highlight */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-[#101436] via-[#1e1b4b] to-[#312e81]" data-testid="home-offer">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-10 lg:p-14">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#ff9800]">
                <Sparkles className="h-3.5 w-3.5" />
                {t("hero.badge")}
              </span>
              <h2 className="mt-5 font-display text-3xl font-bold leading-tight text-white md:text-4xl">
                {t("home.packagesTitle")}
              </h2>
              <p className="mt-4 max-w-lg text-base leading-relaxed text-zinc-300">{t("home.packagesSubtitle")}</p>
              <ul className="mt-8 space-y-3">
                {offerFeatures.map((feat, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-zinc-200">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#ff9800] text-[#1d1d1f]">
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    {feat}
                  </li>
                ))}
              </ul>
              <Link
                to="/tarifeler"
                data-testid="home-offer-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#ff9800] px-7 py-3 text-sm font-bold text-[#1d1d1f] transition-colors hover:bg-[#f57c00]"
              >
                {t("home.allPackages")}
                <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
            </div>
            <div className="relative hidden items-center justify-center p-14 lg:flex">
              <div className="absolute -bottom-12 -end-12 h-40 w-40 rounded-full bg-[#ff9800]/10 blur-3xl" />
              <div className="absolute -top-12 -start-12 h-40 w-40 rounded-full bg-[#7C3AED]/20 blur-3xl" />
              <div className="relative text-center">
                <p className="font-display text-7xl font-extrabold text-white">100</p>
                <p className="text-xl font-bold text-[#ff9800]">Mbps</p>
                <div className="mx-auto mt-4 h-px w-16 bg-white/20" />
                <p className="mt-4 text-sm font-semibold uppercase tracking-[0.2em] text-zinc-400">{t("hero.statSpeed")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      {packages.length > 0 && (
        <section className="bg-[#faf9ff] py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="font-display text-3xl font-bold text-[#101436] md:text-4xl">{t("home.packagesTitle")}</h2>
              <p className="mt-3 text-base text-gray-500 md:text-lg">{t("home.packagesSubtitle")}</p>
            </div>
            <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3" data-testid="home-packages-grid">
              {packages.slice(0, 3).map((pkg) => (
                <PackageCard key={pkg.id} pkg={pkg} />
              ))}
            </div>

            {/* PRICE UPDATE NOTICE */}
            <div
              className="mt-10 flex flex-col gap-4 rounded-2xl border border-[#ff9800]/30 bg-[#fff7ec] p-6 sm:flex-row sm:items-start sm:gap-4"
              data-testid="home-price-notice"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#ff9800]/15 text-[#ff9800]">
                <Info className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display text-sm font-bold uppercase tracking-wider text-[#ff9800]">
                  {t("home.priceNoticeTitle")}
                </p>
                <p className="mt-1.5 text-sm font-semibold leading-relaxed text-gray-800">
                  {t("home.priceNotice")}
                </p>
                <p className="mt-3 text-right text-sm font-bold text-[#101436]">
                  {t("home.priceNoticeDate")}
                </p>
              </div>
            </div>

            {/* PRICE CHANGE TABLE */}
            <div className="mt-8">
              <h3 className="font-display text-lg font-bold text-[#101436] md:text-xl">
                {t("home.priceTable.title")}
              </h3>
              <div className="mt-4 overflow-x-auto rounded-2xl border border-zinc-200 bg-white shadow-sm">
                <table className="w-full min-w-[600px] border-collapse text-sm">
                  <thead>
                    <tr className="bg-[#101436] text-white">
                      <th className="px-4 py-3 text-start font-display text-xs font-bold uppercase tracking-wider sm:text-sm">
                        {t("home.priceTable.colName")}
                      </th>
                      <th className="px-4 py-3 text-end font-display text-xs font-bold uppercase tracking-wider sm:text-sm">
                        {t("home.priceTable.colPrice")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {PRICE_CHANGE_ROWS.map((row, i) => (
                      <tr key={i} className={i % 2 ? "bg-[#faf9ff]" : "bg-white"}>
                        <td className="px-4 py-2.5 text-start leading-snug text-gray-700">{row.name}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-end font-semibold text-gray-800">{row.price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CAMPAIGN GRID */}
      {campaigns.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <h2 className="text-center font-display text-3xl font-bold text-[#101436] md:text-4xl">{t("home.campaignsTitle")}</h2>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="home-campaigns-grid">
            {campaigns.slice(0, 3).map((campaign) => (
              <Link
                key={campaign.id}
                to="/kampanyalar"
                className="group overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-sm transition-shadow hover:shadow-xl"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={campaign.image}
                    alt=""
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block rounded-full bg-[#fff3e0] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#ff9800]">
                    {ml(campaign.badge)}
                  </span>
                  <h3 className="mt-3 font-display text-lg font-bold text-[#101436]">{ml(campaign.title)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-500 line-clamp-2">{ml(campaign.description)}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#4F46E5]">
                    {t("campaigns.apply")}
                    <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* SERVICES — dark navy */}
      {services.length > 0 && (
        <section className="bg-[#101436] py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="font-display text-3xl font-bold text-white md:text-4xl">{t("home.servicesTitle")}</h2>
              <p className="mt-3 text-base text-zinc-400 md:text-lg">{t("home.servicesSubtitle")}</p>
            </div>
            <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-services-grid">
              {services.map((service) => (
                <Link
                  key={service.id}
                  to="/hizmetler"
                  className="group rounded-2xl border border-white/10 bg-white/5 p-7 transition-colors duration-300 hover:border-[#ff9800]"
                >
                  <ServiceIcon name={service.icon} className="h-7 w-7 text-[#ff9800]" />
                  <h3 className="mt-4 font-display text-lg font-bold text-white">{ml(service.title)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400 line-clamp-2">{ml(service.description)}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      <section className="bg-[#faf9ff] py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center font-display text-3xl font-bold text-[#101436] md:text-4xl">
            {lang === "tr" ? "Memnun Müşterilerimiz" : lang === "en" ? "Happy Customers" : lang === "ru" ? "Довольные клиенты" : "عملاؤنا السعداء"}
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {testimonials.map((item, i) => (
              <div key={i} className="rounded-2xl border border-zinc-100 bg-white p-7 shadow-sm">
                <Quote className="h-8 w-8 text-[#ff9800]/40" />
                <p className="mt-4 text-sm leading-relaxed text-gray-600">{item.text}</p>
                <div className="mt-6 flex items-center gap-3 border-t border-zinc-100 pt-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-call font-display font-bold text-white">
                    {item.name.charAt(0)}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-[#101436]">{item.name}</p>
                    <p className="text-xs text-gray-400">EntaNET {lang === "tr" ? "Müşterisi" : "Customer"}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ PREVIEW */}
      {faqs.length > 0 && (
        <section className="mx-auto max-w-3xl px-4 py-20 sm:px-6 lg:px-8">
          <h2 className="text-center font-display text-3xl font-bold text-[#101436] md:text-4xl">{t("faq.title")}</h2>
          <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="home-faq-accordion">
            {faqs.slice(0, 5).map((faq) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="rounded-xl border border-zinc-200 bg-white px-6 data-[state=open]:border-[#ff9800]"
              >
                <AccordionTrigger className="text-start font-display text-base font-bold text-[#101436] hover:no-underline">
                  {ml(faq.question)}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-gray-500">{ml(faq.answer)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-8 text-center">
            <Link to="/sss" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#4F46E5] hover:underline">
              {lang === "tr" ? "Tüm Soruları Gör" : "See All Questions"}
              <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Link>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#101436] via-[#1e1b4b] to-[#312e81] py-20">
        <div className="absolute inset-0 bg-radial-orange" />
        <div className="relative mx-auto max-w-2xl px-4 text-center sm:px-6 lg:px-8">
          <ShieldCheck className="mx-auto h-12 w-12 text-[#ff9800]" />
          <h2 className="mt-5 font-display text-3xl font-bold text-white md:text-4xl">{t("home.ctaTitle")}</h2>
          <p className="mt-4 text-base text-zinc-300 md:text-lg">{t("home.ctaSubtitle")}</p>
          <Link
            to="/iletisim"
            data-testid="home-final-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#ff9800] px-8 py-4 text-sm font-bold text-[#1d1d1f] transition-colors hover:bg-[#f57c00]"
          >
            {t("home.ctaButton")}
            <ArrowRight className="h-4 w-4 rtl:rotate-180" />
          </Link>
        </div>
      </section>
    </div>
  );
}
